import {
  Accordion,
  ActionIcon,
  Anchor,
  Button,
  Card,
  Menu,
  PasswordInput,
  Paper,
  Select,
  TextInput,
  Textarea,
  createTheme,
  type MantineColorsTuple,
} from '@mantine/core';

const brand: MantineColorsTuple = [
  '#f4f3ff',
  '#e9e7ff',
  '#d2ceff',
  '#b9b1ff',
  '#a293ff',
  '#8d78ff',
  '#7d63ff',
  '#6f55ea',
  '#6148cd',
  '#543db1',
];

const accent: MantineColorsTuple = [
  '#fdf1ff',
  '#f9e2ff',
  '#f3c2ff',
  '#ee9cff',
  '#e978ff',
  '#e35bff',
  '#db48f7',
  '#c23cdb',
  '#aa34bf',
  '#932ca6',
];

const appGray: MantineColorsTuple = [
  '#f3f4f6',
  '#e5e7eb',
  '#d1d6e5',
  '#b1b7c9',
  '#8a8fa8',
  '#5662a3',
  '#374151',
  '#1f2937',
  '#111827',
  '#031323',
];

export const appTheme = createTheme({
  primaryColor: 'brand',
  primaryShade: { light: 6, dark: 6 },
  defaultRadius: 'md',
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  fontFamilyMonospace:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  colors: {
    brand,
    accent,
    appGray,
  },
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  radius: {
    xs: '4px',
    sm: '6px',
    md: '8px',
    lg: '16px',
    xl: '24px',
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.1rem',
    xl: '1.25rem',
  },
  lineHeights: {
    xs: '1.2',
    sm: '1.3',
    md: '1.5',
    lg: '1.6',
    xl: '1.6',
  },
  headings: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: '700',
    textWrap: 'balance',
    sizes: {
      h1: { fontSize: '2.5rem', lineHeight: '1.2' },
      h2: { fontSize: '2rem', lineHeight: '1.2' },
      h3: { fontSize: '1.5rem', lineHeight: '1.3' },
      h4: { fontSize: '1.25rem', lineHeight: '1.3' },
      h5: { fontSize: '1.1rem', lineHeight: '1.4' },
      h6: { fontSize: '1rem', lineHeight: '1.4' },
    },
  },
  shadows: {
    xs: '0 1px 3px rgba(0, 0, 0, 0.12)',
    sm: '0 2px 8px rgba(0, 0, 0, 0.2)',
    md: '0 4px 12px rgba(0, 0, 0, 0.25)',
    lg: '0 6px 16px rgba(0, 0, 0, 0.35)',
    xl: '0 10px 24px rgba(0, 0, 0, 0.4)',
  },
  defaultGradient: {
    from: '#5b4df8',
    to: '#a23deb',
    deg: 135,
  },
  other: {
    surface: {
      page: '#071b2d',
      section: '#031323',
      card: '#0f1b32',
      input: '#1a2b47',
      soft: '#111827',
    },
    text: {
      primary: '#ffffff',
      secondary: '#d1d6e5',
      muted: '#b1b7c9',
      dimmed: '#8a8fa8',
    },
    border: {
      subtle: '#5662a3',
      default: '#374151',
    },
    legacy: {
      light: {
        background: '#ffffff',
        text: '#1f2937',
        secondaryBackground: '#f3f4f6',
      },
      dark: {
        background: '#111827',
        text: '#f3f4f6',
        secondaryBackground: '#1f2937',
      },
    },
  },
  components: {
    Button: Button.extend({
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        root: {
          fontWeight: 600,
        },
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        label: { color: 'var(--mantine-color-text)' },
        input: {
          color: 'var(--mantine-color-text)',
          WebkitTextFillColor: 'var(--mantine-color-text)',
          backgroundColor: 'var(--mantine-color-body)',
        },
      },
    }),
    PasswordInput: PasswordInput.extend({
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        label: { color: 'var(--mantine-color-text)' },
        input: {
          color: 'var(--mantine-color-text)',
          WebkitTextFillColor: 'var(--mantine-color-text)',
          backgroundColor: 'var(--mantine-color-body)',
        },
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        label: { color: 'var(--mantine-color-text)' },
        input: {
          color: 'var(--mantine-color-text)',
          WebkitTextFillColor: 'var(--mantine-color-text)',
          backgroundColor: 'var(--mantine-color-body)',
        },
      },
    }),
    Select: Select.extend({
      defaultProps: {
        radius: 'sm',
      },
      styles: {
        label: { color: 'var(--mantine-color-text)' },
        input: {
          color: 'var(--mantine-color-text)',
          WebkitTextFillColor: 'var(--mantine-color-text)',
          backgroundColor: 'var(--mantine-color-body)',
        },
        dropdown: {
          backgroundColor: 'var(--mantine-color-body)',
        },
      },
    }),
    Paper: Paper.extend({
      defaultProps: {
        radius: 'md',
      },
    }),
    Card: Card.extend({
      defaultProps: {
        radius: 'md',
      },
    }),
    Anchor: Anchor.extend({
      defaultProps: {
        underline: 'hover',
      },
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        radius: 'xl',
      },
    }),
    Menu: Menu.extend({
      defaultProps: {
        shadow: 'md',
      },
    }),
    Accordion: Accordion.extend({
      defaultProps: {
        variant: 'separated',
        chevronPosition: 'right',
      },
    }),
  },
});

export const layoutTokens = {
  maxContentWidth: 1000,
  maxTextWidth: 800,
};
